import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import { pickTracksForClip, toUiTracks, parseTrackTitle } from "../../utils/musicMatch";
import "./CoachingPanel.css";

// ClipCoach detail — minimal editor brief.
// Layout: creator cover image up top, three story-beat cards in the
// middle (HOOK / RETENTION / PAYOFF), info + music carousel in the
// right sidebar, CONTENT LINK + Got it at the bottom.
//
// No prose, no "why I'd clip this", no typewriter. Editors come here
// for timecodes + transcript snippets + music. Everything else is noise.

const YT_HOSTS = ["youtube.com", "www.youtube.com", "m.youtube.com", "youtu.be"];

function fmtTsLong(sec) {
  const t = Math.floor(sec || 0);
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = t % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function extractYouTubeId(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (!YT_HOSTS.includes(u.hostname)) return null;
    if (u.hostname === "youtu.be") return u.pathname.replace(/^\//, "").split("/")[0] || null;
    const v = u.searchParams.get("v");
    if (v) return v;
    const m = u.pathname.match(/^\/(?:embed|shorts|live|v)\/([^/?#]+)/);
    return m ? m[1] : null;
  } catch (_) {
    return null;
  }
}

function buildSourceLink(videoUrl, startSec) {
  if (!videoUrl) return null;
  const t = Math.floor(startSec || 0);
  const yt = extractYouTubeId(videoUrl);
  if (yt) return `https://www.youtube.com/watch?v=${yt}&t=${t}s`;
  return videoUrl;
}

// Strip the platform-URL wrapper off whatever the job stored as the
// streamer name. ForYouPage sends `youtube.com/@amir` to the backend
// at generation time, so jobs come back URL-shaped — for display in
// the editor brief we want just the human handle ("Amir").
export function cleanCreatorName(raw) {
  if (!raw) return "";
  const s = String(raw).trim();
  const at = s.lastIndexOf("@");
  if (at >= 0) return s.slice(at + 1).split(/[\s/?#]/)[0];
  const slash = s.lastIndexOf("/");
  if (slash >= 0) return s.slice(slash + 1).split(/[\s?#]/)[0];
  return s;
}

// Editor-style title slug from the clip's hook line — matches the
// Trello card naming convention ("CREATOR CLIP 113 DINESH ISRAEL")
// so editors can sight-read which moment they're working on.
const TITLE_STOPWORDS = new Set([
  "the","and","but","for","with","that","this","from","have","has","had","been","were",
  "was","when","what","just","like","going","gonna","because","about","they","them",
  "their","there","then","than","some","into","over","your","you","our","ours","its",
  "it's","i'm","i've","i'll","don't","didn't","won't","cant","can't","also","much",
  "very","really","said","says","saying","get","got","make","made","one","two","why",
  "how","who","are","not","yes","no","all","any","off","out","up","down","came",
  "come","created","create","made","make","do","did","does","does","an","a","of","to",
  "in","on","at","is","be","or","so","if","as","my","we","he","she","him","her","us",
]);
export function deriveTopic(clip) {
  const text = (clip?.hookLine || clip?.hook || "").trim();
  if (!text) return "";
  return text
    .replace(/[""''""'']/g, "")
    .split(/[\s\-—–.,!?:;()]+/)
    .filter((w) => w.length > 2 && !TITLE_STOPWORDS.has(w.toLowerCase()))
    .slice(0, 3)
    .join(" ")
    .toUpperCase();
}

export default function CoachingPanel({
  clip, index, job, catalog = [],
  open, onClose, onDelete,
  // Navigation is owned by ForYouPage now so we can cycle across the
  // whole flat clip list (Clipper + ClipFinder cards combined), not
  // just within a single job's siblings.
  onPrev, onNext, hasPrev = false, hasNext = false,
}) {
  const displayClip = clip;

  // Body scroll lock + Esc / ← ↑ / → ↓ keyboard nav (delegates to props)
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if ((e.key === "ArrowLeft"  || e.key === "ArrowUp")   && hasPrev) { e.preventDefault(); onPrev?.(); }
      else if ((e.key === "ArrowRight" || e.key === "ArrowDown") && hasNext) { e.preventDefault(); onNext?.(); }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose, onPrev, onNext, hasPrev, hasNext]);

  // Audio-feature music match — STRICT, no fallback. The user needs
  // to be able to tell whether the new musicTarget-driven matcher is
  // actually firing, so we never silently fall back to mood-cascade
  // tracks. Empty result → user sees a clear status message instead.
  const matched = useMemo(() => {
    const { tracks, mood, source } = pickTracksForClip(catalog, displayClip, 5);
    return { tracks: toUiTracks(tracks), mood, source };
  }, [catalog, displayClip]);

  // Mobile swipe nav — only fires when the user *didn't* scroll the
  // long data sheet. Delegates to the same onPrev/onNext props the
  // chevrons + arrow keys use.
  const overlayRef = useRef(null);
  const touchStart = useRef({ x: 0, y: 0, t: 0, scrollTop: 0 });
  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchStart.current = {
      x: t.clientX,
      y: t.clientY,
      t: Date.now(),
      scrollTop: overlayRef.current?.scrollTop ?? 0,
    };
  };
  const onTouchEnd = (e) => {
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStart.current.x;
    const dy = t.clientY - touchStart.current.y;
    const dt = Date.now() - touchStart.current.t;
    const scrolled = Math.abs((overlayRef.current?.scrollTop ?? 0) - touchStart.current.scrollTop);
    if (dt > 600) return;
    if (scrolled > 4) return; // gesture was a scroll, not a nav swipe
    if (Math.abs(dy) > 70 && Math.abs(dy) > Math.abs(dx) * 1.4) {
      if (dy < 0 && hasNext) onNext?.();
      else if (dy > 0 && hasPrev) onPrev?.();
    }
  };

  if (!open) return null;

  const startSec   = displayClip?.start_sec ?? displayClip?.start ?? 0;
  const endSec     = displayClip?.end_sec   ?? displayClip?.end   ?? 0;
  const sourceLink = buildSourceLink(job?.videoUrl, startSec);

  // Beat timecode ranges. Worker emits explicit *EndSec fields when it
  // can — those reflect the natural speech boundary (end-of-sentence,
  // breath pause) and are what the editor actually wants to cut on.
  // For legacy clips without them, fall back to "end-of-this = start-
  // of-next" derivation so old jobs still show useful ranges.
  const hookSec      = displayClip?.hookSec ?? startSec;
  const retentionSec = displayClip?.retentionSec;
  const payoffSec    = displayClip?.payoffSec;
  const hookEnd      = displayClip?.hookEndSec      ?? retentionSec ?? Math.min(hookSec + 3, endSec || hookSec + 3);
  const retentionEnd = displayClip?.retentionEndSec ?? payoffSec    ?? endSec;
  const payoffEnd    = displayClip?.payoffEndSec    ?? endSec;

  // Cover image for the header
  const coverSrc = job?.videoThumbnail || null;
  const brandName = cleanCreatorName(job?.streamerName || job?.videoAuthor || "");

  const tracks   = matched.tracks;
  const moodPair = matched.mood;
  // Human-readable status when the strict matcher couldn't return
  // tracks — surfaces *why* so the user can debug the new pipeline
  // (worker not emitting musicTarget? catalog not backfilled?).
  const matchStatusMsg = (() => {
    switch (matched.source) {
      case "no-spec": return "This clip is missing musicTarget — regenerate it to get vibe-matched music.";
      case "no-data": return "No catalog tracks have audio features yet — run the backfill script to enable matching.";
      case "no-pool": return `No catalog tracks tagged with this clip's inferred mood.`;
      case "empty":   return "Catalog is loading…";
      default:        return "";
    }
  })();

  // Delete the currently-viewed moment, then step to the next clip
  // in the global list (or close if we were at the end).
  const handleDelete = () => {
    if (!onDelete) return;
    if (!window.confirm("Delete this moment?")) return;
    onDelete({ stopPropagation: () => {} });
    if (hasNext) onNext?.();
    else if (hasPrev) onPrev?.();
    else onClose?.();
  };

  return createPortal(
    <div className="ccp-overlay" ref={overlayRef} onClick={onClose} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <button
        className="ccp-float-close"
        onClick={(e) => { e.stopPropagation(); onClose?.(); }}
        aria-label="Close"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      {/* Prev / next nav — arrow keys + chevrons. Nav is global across
          all cards in the feed (Clipper + ClipFinder combined), driven
          by ForYouPage. Hidden when neither side has a sibling. */}
      {(hasPrev || hasNext) && (
        <>
          <button
            type="button"
            className="ccp-nav ccp-nav--prev"
            onClick={(e) => { e.stopPropagation(); onPrev?.(); }}
            disabled={!hasPrev}
            aria-label="Previous clip"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button
            type="button"
            className="ccp-nav ccp-nav--next"
            onClick={(e) => { e.stopPropagation(); onNext?.(); }}
            disabled={!hasNext}
            aria-label="Next clip"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </>
      )}

      {/* ── CENTER: editor brief ── */}
      <main className="ccp-main" onClick={(e) => e.stopPropagation()}>
        <div className="ccp-main-inner" key={index /* re-trigger entrance animation on nav */}>
          {/* Trello-style cover strip with the creator's thumbnail */}
          {coverSrc && (
            <div className="ccp-cover">
              <img src={coverSrc} alt={brandName} className="ccp-cover-img" />
            </div>
          )}

          <header className="ccp-header">
            <img src="/favicon.png" alt="" className="ccp-fish" />
            <div className="ccp-header-text">
              <div className="ccp-header-title">
                {(brandName || "Clip").toUpperCase()} CLIP {(index ?? 0) + 1}
                {(() => {
                  const topic = deriveTopic(displayClip);
                  return topic ? ` ${topic}` : "";
                })()}
              </div>
            </div>
          </header>

          {/* Optional rendered preview if the user opted into renders */}
          {displayClip?.url && (
            <video
              src={displayClip.url}
              controls
              playsInline
              className="ccp-rendered-video"
            />
          )}

          {/* HOOK — first 3 seconds */}
          {(displayClip?.hookLine || displayClip?.hook) && (
            <section className="ccp-layer">
              <div className="ccp-layer-head">
                <span className="ccp-layer-label">Hook</span>
                <span className="ccp-layer-tc">
                  [{fmtTsLong(hookSec)}] — [{fmtTsLong(hookEnd)}]
                </span>
              </div>
              <div className="ccp-layer-quote">
                "{displayClip.hookLine || displayClip.hook}"
              </div>
            </section>
          )}

          {/* RETENTION — the bridge */}
          {displayClip?.retentionLine && (
            <section className="ccp-layer">
              <div className="ccp-layer-head">
                <span className="ccp-layer-label">Retention · the bridge</span>
                {retentionSec != null && (
                  <span className="ccp-layer-tc">[{fmtTsLong(retentionSec)}] — [{fmtTsLong(retentionEnd)}]</span>
                )}
              </div>
              <div className="ccp-layer-quote">
                "{displayClip.retentionLine}"
              </div>
            </section>
          )}

          {/* PAYOFF — substantial final-10s landing */}
          {displayClip?.payoffLine && (
            <section className="ccp-layer ccp-layer--payoff">
              <div className="ccp-layer-head">
                <span className="ccp-layer-label ccp-layer-label--payoff">Payoff · final 10s</span>
                {payoffSec != null && (
                  <span className="ccp-layer-tc">[{fmtTsLong(payoffSec)}] — [{fmtTsLong(payoffEnd)}]</span>
                )}
              </div>
              <div className="ccp-layer-quote ccp-layer-quote--payoff">
                "{displayClip.payoffLine}"
              </div>
            </section>
          )}

          {/* Empty-state for clips that pre-date the structured worker
              output — gently nudge the user that this moment needs to
              be re-generated to get the full brief. */}
          {!displayClip?.hookLine && !displayClip?.hook && !displayClip?.retentionLine && !displayClip?.payoffLine && (
            <section className="ccp-empty-brief">
              This moment was generated before the editor-brief upgrade.
              Re-run the clip in ClipFinder to get hook / retention / payoff cards.
            </section>
          )}
        </div>
      </main>

      {/* ── RIGHT: info + music sidebar ── */}
      <aside
        className="ccp-sidebar"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ccp-sidebar-grip" aria-hidden="true">
          <span className="ccp-sidebar-grip-pill" />
        </div>

        {/* Information — lean. Timecode intentionally omitted: hook /
            retention / payoff sections each carry their own [m:ss] so
            the start→end range was just visual noise. */}
        <div className="ccp-section">
          <div className="ccp-label">Information</div>
          <div className="ccp-info-list">
            {job?.platform && (
              <div className="ccp-info-row">
                <span className="ccp-info-key">Platform</span>
                <span className="ccp-info-val">{String(job.platform).replace(/^./, (c) => c.toUpperCase())}</span>
              </div>
            )}
            {brandName && (
              <div className="ccp-info-row">
                <span className="ccp-info-key">Creator</span>
                <span className="ccp-info-val">{brandName}</span>
              </div>
            )}
          </div>
        </div>

        {/* AI Caption — ready-to-post copy generated by the worker.
            Same field (clip.caption) the Streamer-mode panel reads. */}
        {displayClip?.caption && (
          <div className="ccp-section">
            <div className="ccp-caption-head">
              <span className="ccp-caption-badge">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 12l2 2 4-4"/>
                  <circle cx="12" cy="12" r="10"/>
                </svg>
                AI caption
              </span>
              <button
                type="button"
                className="ccp-caption-copy"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(displayClip.caption);
                    toast.success("Caption copied!");
                  } catch {
                    toast.error("Couldn't copy — copy it manually");
                  }
                }}
                aria-label="Copy caption to clipboard"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                </svg>
                Copy
              </button>
            </div>
            <div className="ccp-caption-body">{displayClip.caption}</div>
          </div>
        )}

        {/* Music recs — strict, no fallback. Empty state tells the
            user exactly which condition failed (musicTarget missing,
            no audio-features in catalog, etc.) so the new pipeline
            is debuggable from the UI. */}
        {tracks.length === 0 && matchStatusMsg && (
          <div className="ccp-section">
            <div className="ccp-music-head">
              <span className="ccp-music-badge">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18V5l12-2v13" />
                  <circle cx="6" cy="18" r="3" />
                  <circle cx="18" cy="16" r="3" />
                </svg>
                Music to pair
              </span>
            </div>
            <div className="ccp-music-empty">{matchStatusMsg}</div>
          </div>
        )}

        {tracks.length > 0 && (
          <div className="ccp-section">
            <div className="ccp-music-head">
              <span className="ccp-music-badge">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18V5l12-2v13" />
                  <circle cx="6" cy="18" r="3" />
                  <circle cx="18" cy="16" r="3" />
                </svg>
                Music to pair{moodPair ? ` · ${moodPair}` : ""}
              </span>
            </div>
            <div className="ccp-music-row">
              {tracks.map((t, i) => {
                const { displayName, modifiers } = parseTrackTitle(t.trackName);
                return (
                <div key={t.trackId || i} className="ccp-music-card">
                  <div className="ccp-music-meta">
                    <div className="ccp-music-name">{displayName}</div>
                    {t.artist && <div className="ccp-music-artist">{t.artist}</div>}
                    {modifiers.length > 0 && (
                      <div className="ccp-music-mods">
                        {modifiers.map((m) => (
                          <span key={m} className="ccp-music-mod">{m}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="ccp-music-links">
                    {t.tiktokSoundUrl && (
                      <a href={t.tiktokSoundUrl} target="_blank" rel="noopener noreferrer" className="ccp-music-link">
                        TikTok
                      </a>
                    )}
                    {t.spotifyUrl && (
                      <a href={t.spotifyUrl} target="_blank" rel="noopener noreferrer" className="ccp-music-link">
                        Spotify
                      </a>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="ccp-spacer" />

        {/* Action stack — mirrors ClipDetailPanel: pulsing orange CTA
            up top, small delete button underneath. */}
        <div className="ccp-actions">
          {sourceLink && (
            <a
              href={sourceLink}
              target="_blank"
              rel="noopener noreferrer"
              className="ccp-btn-orange"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
              Content link
            </a>
          )}
          {onDelete && (
            <button type="button" className="ccp-btn-delete-small" onClick={handleDelete}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
              Delete
            </button>
          )}
        </div>
      </aside>
    </div>,
    document.body
  );
}
